import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {isEmpty} from 'ramda'
import XLSX from 'xlsx'
import {lighten, makeStyles} from '@material-ui/core/styles'
import {
    Box,
    Button,
    IconButton,
    InputBase,
    Typography,
    Toolbar,
    Paper,
} from '@material-ui/core'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TablePagination from '@material-ui/core/TablePagination'
import TableRow from '@material-ui/core/TableRow'
import TableSortLabel from '@material-ui/core/TableSortLabel'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Switch from '@material-ui/core/Switch'

//******** Images *************************
import SearchIcon from '@material-ui/icons/Search'
import FlagIcon from '@material-ui/icons/Flag'
import Close from '@material-ui/icons/Close'
import {ReactComponent as DownloadIcon} from '../../../../images/download-icon.svg'
import selectEvent from '../../../../images/selectEvent.svg'

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1
    }
    if (b[orderBy] > a[orderBy]) {
        return 1
    }
    return 0
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy)
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index])
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0])
        if (order !== 0) return order
        return a[1] - b[1]
    })
    return stabilizedThis.map(el => el[0])
}

const headCells = [
    {
        id: 'euid',
        numeric: false,
        disablePadding: false,
        label: 'EUID',
    },
    {
        id: 'firstName',
        numeric: false,
        disablePadding: false,
        label: 'Name',
    },
    {
        id: 'deptName',
        numeric: false,
        disablePadding: false,
        label: 'Department Name',
    },
    {
        id: 'approle',
        numeric: false,
        disablePadding: false,
        label: 'Role',
    },
    {
        id: 'lastLoginDate',
        numeric: false,
        disablePadding: false,
        label: 'Last Logged In',
    },
    {
        id: 'loggedin2Years',
        numeric: false,
        disablePadding: false,
        label: 'Inactive > 2 years',
    },
]

function EnhancedTableHead(props) {
    const {classes, order, orderBy, onRequestSort} = props
    const createSortHandler = property => event => {
        onRequestSort(event, property)
    }

    return (
        <TableHead>
            <TableRow>
                {headCells.map(headCell => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'default'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <span className={classes.visuallyHidden}>
                                    {order === 'desc'
                                        ? 'sorted descending'
                                        : 'sorted ascending'}
                                </span>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    )
}

EnhancedTableHead.propTypes = {
    classes: PropTypes.object.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
}

const useToolbarStyles = makeStyles(theme => ({
    root: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(1),
    },
    highlight:
        theme.palette.type === 'light'
            ? {
                  color: theme.palette.secondary.main,
                  backgroundColor: lighten(theme.palette.secondary.light, 0.85),
              }
            : {
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.secondary.dark,
              },
    title: {
        flex: '1 1 100%',
    },

    buttonlabel: {
        width: '400px',
        whiteSpace: 'nowrap',
        margin: 0,
    },
    filterlabel: {
        marginTop: '-5px',
    },
    searchBox: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        border: '1px solid',
        borderColor: theme.palette.grey.medium,
        borderRadius: theme.shape.borderRadius,
        margin: '0 0 0 5px',
        height: '35px',
    },
    searchInput: {
        flex: 1,
        width: '250px',
        margin: '0 10px',
    },
    clearButton: {
        padding: 8,
        color: theme.palette.grey.medium,
    },
}))

const EnhancedTableToolbar = props => {
    const classes = useToolbarStyles()
    const {userName, searchResults, searchTerm, setSearchTerm} = props

    const handleChange = e => {
        setSearchTerm(e.target.value)
    }

    const handleClearSearch = () => {
        setSearchTerm('')
    }

    // generate report file for download
    const handleDownloadExcel = () => {
        const wb = XLSX.utils.book_new()
        wb.Props = {
            Title: 'Report-Cardswipe-Role-Audit',
            Author: userName,
            CreatedDate: new Date(),
        }

        wb.SheetNames.push('All Users')
        const ws_data_allusers =
            searchResults.length > 0
                ? searchResults.map(item => {
                      return {
                          EUID: item.euid,
                          Name: item.firstName + ' ' + item.lastName,
                          'Email Address': item.emailAddress,
                          'Department Name': item.deptName,
                          Role: item.approle,
                          'Last Login Date': item.lastLogin
                              ? item.lastLoginDate
                              : 'None Found',
                          'Inactive > 2 years': item.loggedin2Years
                              ? 'No'
                              : 'Yes',
                      }
                  })
                : [{'User Records': 'None'}]
        const ws_allusers = XLSX.utils.json_to_sheet(ws_data_allusers)
        wb.Sheets['All Users'] = ws_allusers

        XLSX.writeFile(wb, 'Report-Cardswipe-Role-Audit.xlsx')
    }

    return (
        <Toolbar className={classes.root}>
            <Typography
                className={classes.title}
                align="left"
                variant="h4"
                id="tableTitle"
                component="div"
            >
                Role Audits
            </Typography>

            <>
                <Button
                    variant="outlined"
                    color="primary"
                    aria-label="download audit report"
                    onClick={() => handleDownloadExcel()}
                    size="small"
                    startIcon={
                        <DownloadIcon
                            style={{
                                color: '#098447',
                                height: '30px',
                                padding: '0',
                            }}
                        />
                    }
                    style={{
                        margin: '0 5px',
                        height: '35px',
                        width: '480px',
                        boxShadow: 'none',
                    }}
                    classes={{
                        label: classes.buttonlabel,
                    }}
                >
                    DOWNLOAD ROLE AUDIT
                </Button>
                <Box className={classes.searchBox}>
                    <InputBase
                        className={classes.searchInput}
                        placeholder="Search"
                        inputProps={{
                            'aria-label': 'search user roles',
                        }}
                        value={searchTerm}
                        onChange={handleChange}
                    />
                    {searchTerm && (
                        <IconButton
                            className={classes.clearButton}
                            onClick={() => handleClearSearch()}
                        >
                            {' '}
                            <Close />{' '}
                        </IconButton>
                    )}
                    {!searchTerm && <SearchIcon style={{margin: '0 5px'}} />}
                </Box>
            </>
        </Toolbar>
    )
}

const useStyles = makeStyles(theme => ({
    root: {
        margin: '35px 5% 0 5%',
        [theme.breakpoints.down('sm')]: {
            margin: '35px 10% 0 10%',
        },
    },
    paper: {
        width: '100%',
        marginBottom: theme.spacing(2),
        boxShadow: 'none',
    },
    table: {
        minWidth: 750,
    },
    tablebody: {
        overflowY: 'auto',
        maxHeight: '65vh',
    },
    visuallyHidden: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        top: 20,
        width: 1,
    },
    emptycontainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        width: '-webkit-fill-available',
        height: '65vh',
    },
}))

export default function AuditAllDeptRolesTable(props) {
    const classes = useStyles()
    const [order, setOrder] = React.useState('asc')
    const [orderBy, setOrderBy] = React.useState('Name')
    const [page, setPage] = React.useState(0)
    const [dense, setDense] = React.useState(true)
    const [rowsPerPage, setRowsPerPage] = React.useState(10)

    const {userName, allusers} = props

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const handleChangePage = (event, newPage) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = event => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const handleChangeDense = event => {
        setDense(event.target.checked)
    }

    const emptyRows =
        rowsPerPage -
        Math.min(rowsPerPage, allusers.length - page * rowsPerPage)

    const [searchTerm, setSearchTerm] = useState('')
    const [searchResults, setSearchResults] = useState(allusers)

    useEffect(() => {
        const results = allusers.filter(
            user =>
                user.firstName
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                user.lastName
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                user.euid.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (user.approle
                    ? user.approle
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase())
                    : false) ||
                (user.deptName
                    ? user.deptName
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase())
                    : false) ||
                user.lastLoginDate
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
        )
        setSearchResults(results)
    }, [allusers, searchTerm])

    return (
        <div className={classes.root}>
            <Paper className={classes.paper}>
                <EnhancedTableToolbar
                    userName={userName}
                    searchResults={searchResults}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                />

                {isEmpty(allusers) ? (
                    <Box className={classes.emptycontainer}>
                        <img src={selectEvent} alt="select event" />
                        <Typography align="center" variant="h5">
                            Create a new Department User
                        </Typography>
                        <Typography align="center" variant="body1">
                            Click on the DEPARTMENT ROLES
                        </Typography>
                        <Typography align="center" variant="body1">
                            sub-menu above.
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table
                            className={classes.table}
                            aria-labelledby="tableTitle"
                            size={dense ? 'small' : 'medium'}
                            aria-label="enhanced table"
                        >
                            <EnhancedTableHead
                                classes={classes}
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                                rowCount={allusers.length}
                            />
                            <TableBody className={classes.tablebody}>
                                {stableSort(
                                    searchResults,
                                    getComparator(order, orderBy)
                                )
                                    .slice(
                                        page * rowsPerPage,
                                        page * rowsPerPage + rowsPerPage
                                    )
                                    .map(row => {
                                        return (
                                            <TableRow
                                                tabIndex={-1}
                                                key={
                                                    row.deptName +
                                                    row.euid +
                                                    row.role
                                                }
                                            >
                                                <TableCell
                                                    id={row.euid}
                                                    align="left"
                                                    style={{
                                                        textTransform: 'none',
                                                    }}
                                                >
                                                    {row.euid}
                                                </TableCell>
                                                <TableCell
                                                    id={
                                                        row.firstName +
                                                        row.lastName
                                                    }
                                                    align="left"
                                                >
                                                    {row.firstName +
                                                        ' ' +
                                                        row.lastName}
                                                </TableCell>
                                                <TableCell
                                                    id={row.deptName}
                                                    align="left"
                                                >
                                                    {row.deptName}
                                                </TableCell>
                                                <TableCell
                                                    id={row.approle}
                                                    align="left"
                                                    style={{
                                                        textTransform:
                                                            'Capitalize',
                                                    }}
                                                >
                                                    {row.approle}
                                                </TableCell>
                                                <TableCell
                                                    id={row.lastLoginDate}
                                                    align="left"
                                                >
                                                    {row.lastLoginDate}
                                                </TableCell>
                                                <TableCell
                                                    id={'loggedin2Years'}
                                                    align="center"
                                                >
                                                    {row.loggedin2Years ? (
                                                        ''
                                                    ) : (
                                                        <FlagIcon
                                                            style={{
                                                                color:
                                                                    '#F96157',
                                                            }}
                                                        />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                {emptyRows > 0 && (
                                    <TableRow
                                        style={{
                                            height:
                                                (dense ? 33 : 53) * emptyRows,
                                        }}
                                    >
                                        <TableCell colSpan={6} />
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {!isEmpty(searchResults) && (
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={searchResults.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                )}
            </Paper>
            {!isEmpty(allusers) && (
                <FormControlLabel
                    control={
                        <Switch checked={dense} onChange={handleChangeDense} />
                    }
                    label="Dense padding"
                />
            )}
        </div>
    )
}
